import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, RotateCcw, FileText } from "lucide-react";
import { api, apiError, num, dateTime } from "@/lib/api";
import { AvailabilityBlock } from "@/components/Availability";
import { AppShell, PageHeader, Panel, SkeletonRows, EmptyState, Btn, Tag } from "@/components/Shell";

const STATUS_TONE = { AUTHORISED: "green", DRAFT: "slate", VOIDED: "red" };

export default function Invoices() {
  const [invoices, setInvoices] = useState(null);
  const [products, setProducts] = useState([]);
  const [log, setLog] = useState([]);
  const [unmatched, setUnmatched] = useState([]);
  const [lines, setLines] = useState([{ sku: "", quantity: 10 }]);
  const [status, setStatus] = useState("AUTHORISED");
  const [contact, setContact] = useState("Harborview Wholesale");
  const [events, setEvents] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [inv, prod, l, u] = await Promise.all([
      api.get("/invoices"),
      api.get("/products", { params: { page: 1, page_size: 100 } }),
      api.get("/sync-log", { params: { limit: 40 } }),
      api.get("/unmatched-lines"),
    ]);
    setInvoices(inv.data);
    setProducts(prod.data.items);
    setLog(l.data);
    setUnmatched(u.data);
    if (!lines[0].sku && prod.data.items[0]) {
      setLines([{ sku: prod.data.items[0].sku, quantity: 10 }]);
    }
  };

  useEffect(() => {
    load().catch((e) => toast.error(apiError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const simulate = async () => {
    setBusy(true);
    try {
      const payload = {
        contact_name: contact,
        status,
        lines: lines.filter((l) => l.sku).map((l) => ({ sku: l.sku, quantity: Number(l.quantity) })),
      };
      const { data } = await api.post("/xero/invoices/simulate", payload);
      setEvents(data);
      toast.success(data.committed ? `${data.number} committed stock` : `${data.number} created — stock untouched`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const act = async (path, msg) => {
    try {
      await api.post(path);
      toast.success(msg);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <AppShell>
      <PageHeader
        title="Xero invoices"
        subtitle="Authorised invoices commit stock. Drafts and quotes never touch it. Every event is idempotent per invoice ID and version."
        testId="invoices-header"
      />

      <div className="grid gap-4 xl:grid-cols-12">
        <div className="space-y-4 xl:col-span-5">
          <Panel title="Simulate a Xero invoice webhook" testId="simulate-panel">
            <div className="space-y-3 p-4">
              <label className="block text-sm">
                Customer
                <input
                  data-testid="simulate-contact-input"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  className="mt-1 w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
                />
              </label>
              <label className="block text-sm">
                Invoice status
                <select
                  data-testid="simulate-status-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  className="mt-1 w-full border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
                >
                  <option value="AUTHORISED">AUTHORISED — commits stock</option>
                  <option value="DRAFT">DRAFT / quote — no stock effect</option>
                </select>
              </label>

              {lines.map((line, i) => (
                <div key={i} className="flex items-end gap-2">
                  <label className="flex-1 text-sm">
                    {i === 0 && "Line items"}
                    <select
                      data-testid={`simulate-line-sku-${i}`}
                      value={line.sku}
                      onChange={(e) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, sku: e.target.value } : l)))}
                      className="mono mt-1 w-full border border-slate-300 bg-white px-2 py-2 text-xs focus:border-slate-900 focus:outline-none"
                    >
                      {products.map((p) => (
                        <option key={p.id} value={p.sku}>{p.sku} — {p.name}</option>
                      ))}
                    </select>
                  </label>
                  <input
                    data-testid={`simulate-line-qty-${i}`}
                    type="number"
                    min="1"
                    value={line.quantity}
                    onChange={(e) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, quantity: e.target.value } : l)))}
                    className="mono w-20 border border-slate-300 px-2 py-2 text-xs focus:border-slate-900 focus:outline-none"
                  />
                  {lines.length > 1 && (
                    <button data-testid={`simulate-remove-line-${i}`} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} className="p-2 text-slate-400 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}

              <Btn variant="ghost" data-testid="simulate-add-line" onClick={() => setLines((ls) => [...ls, { sku: products[0]?.sku || "", quantity: 5 }])}>
                <Plus className="h-4 w-4" /> Add line
              </Btn>

              <Btn className="w-full justify-center" data-testid="simulate-invoice-button" disabled={busy} onClick={simulate}>
                {busy ? "Sending webhook…" : "Authorise in Xero"}
              </Btn>
            </div>
          </Panel>

          {events && (
            <Panel title={`${events.number} — commit result`} testId="commit-result">
              {events.committed ? (
                <div className="divide-y divide-slate-100">
                  {events.events?.map((ev) => (
                    <div key={ev.sku} className="flash p-4" data-testid={`commit-event-${ev.sku}`}>
                      <p className="mono mb-2 text-xs text-slate-500">{ev.sku} · {ev.quantity} units committed</p>
                      <AvailabilityBlock data={ev.availability} testIdPrefix={`commit-${ev.sku}`} compact />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="p-4 text-sm text-slate-600" data-testid="commit-skipped">
                  {events.message} — quotes and drafts never affect stock.
                </p>
              )}
              {events.unmatched > 0 && (
                <p className="border-t border-slate-200 bg-amber-50 px-4 py-2 text-xs text-amber-800" data-testid="commit-unmatched-note">
                  {events.unmatched} line(s) had no matching product and went to the unmatched queue.
                </p>
              )}
            </Panel>
          )}

          <Panel title={`Unmatched line queue (${unmatched.length})`} testId="unmatched-panel">
            {unmatched.length === 0 ? (
              <EmptyState title="Nothing unmatched" body="Every invoice line has mapped to a Stock360 product." testId="unmatched-empty" />
            ) : (
              <div className="divide-y divide-slate-100">
                {unmatched.map((u) => (
                  <div key={u.id} className="flex flex-wrap items-center gap-2 p-3 text-sm" data-testid={`unmatched-row-${u.sku}`}>
                    <span className="mono flex-1 text-xs">{u.invoice_number} · {u.sku} × {u.quantity}</span>
                    <select
                      data-testid={`unmatched-map-select-${u.sku}`}
                      defaultValue=""
                      onChange={async (e) => {
                        if (!e.target.value) return;
                        try {
                          await api.post(`/unmatched-lines/${u.id}/resolve`, { product_id: e.target.value });
                          toast.success("Line mapped and stock applied");
                          await load();
                        } catch (err) {
                          toast.error(apiError(err));
                        }
                      }}
                      className="border border-slate-300 bg-white px-2 py-1.5 text-xs"
                    >
                      <option value="">Map to product…</option>
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>{p.sku}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>

        <div className="space-y-4 xl:col-span-7">
          <Panel title="Invoices" testId="invoices-panel">
            {!invoices ? (
              <SkeletonRows rows={5} />
            ) : invoices.length === 0 ? (
              <EmptyState icon={FileText} title="No invoices yet" body="Simulate a Xero invoice to see stock commit live." testId="invoices-empty" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
                    <tr>
                      <th className="px-4 py-2 text-left">Invoice</th>
                      <th className="px-3 py-2 text-left">Customer</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-left">Stock</th>
                      <th className="px-3 py-2 text-right">Lines</th>
                      <th className="px-3 py-2 text-left">When</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {invoices.map((inv) => (
                      <tr key={inv.id} className="hover:bg-slate-50" data-testid={`invoice-row-${inv.number}`}>
                        <td className="mono px-4 py-2">{inv.number}</td>
                        <td className="px-3 py-2 text-slate-700">{inv.contact_name}</td>
                        <td className="px-3 py-2"><Tag tone={STATUS_TONE[inv.status] || "slate"}>{inv.status}</Tag></td>
                        <td className="px-3 py-2">
                          {inv.committed ? (
                            inv.fulfilment_mode === "manual" && !inv.fulfilled ? <Tag tone="amber">Committed</Tag> : <Tag tone="blue">Deducted</Tag>
                          ) : (
                            <Tag>Untouched</Tag>
                          )}
                        </td>
                        <td className="num px-3 py-2 text-right">{inv.resolved_lines?.length || 0}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{dateTime(inv.created_at)}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex justify-end gap-1">
                            {inv.committed && inv.fulfilment_mode === "manual" && !inv.fulfilled && (
                              <Btn variant="outline" className="px-2 py-1 text-xs" data-testid={`fulfil-${inv.number}`} onClick={() => act(`/invoices/${inv.id}/fulfil`, "Fulfilled")}>
                                Fulfil
                              </Btn>
                            )}
                            {inv.status !== "VOIDED" && (
                              <Btn variant="danger" className="px-2 py-1 text-xs" data-testid={`void-${inv.number}`} onClick={() => act(`/invoices/${inv.id}/void`, "Voided and stock reversed")}>
                                Void
                              </Btn>
                            )}
                            <Btn variant="outline" className="px-2 py-1 text-xs" data-testid={`credit-${inv.number}`} onClick={() => act(`/invoices/${inv.id}/credit-note`, "Credit note recorded")}>
                              <RotateCcw className="h-3 w-3" /> Credit
                            </Btn>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <Panel title="Sync & audit log" testId="sync-log-panel">
            <div className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
              {log.length === 0 && <EmptyState title="No sync events" body="Xero and Stock360 events appear here." testId="sync-log-empty" />}
              {log.map((l) => (
                <div key={l.id} className="flex items-start gap-3 px-4 py-2.5 text-sm" data-testid="sync-log-row">
                  <Tag tone={l.status === "success" ? "green" : l.status === "skipped" ? "slate" : "amber"}>{l.status}</Tag>
                  <div className="min-w-0 flex-1">
                    <p className="text-slate-700">{l.message}</p>
                    <p className="mono text-[11px] text-slate-400">{l.source} · {l.action} · {dateTime(l.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      </div>
    </AppShell>
  );
}
